/**
 * GlobalNavigation.js
 *
 * Left sidebar navigation for the entire app. Renders a permanent MUI Drawer
 * (always visible, never collapses) containing a two-tier hierarchy:
 *
 *   Category (collapsible group header)
 *     -> Nav Item (clickable row)
 *
 * Categories displayed — follows engineering lifecycle order:
 *   "Workspace"           -> Projects
 *   Analytics              (standalone, between workspace and lifecycle)
 *   "Engineering cycle"   -> Requirements, CAD, Compliance, Verification, Documents
 *
 * Orchestrator and Knowledge Base are standalone items at the bottom.
 *
 * Interaction with App.js:
 *   - Receives `activeSection` (string) and `onSectionChange` (callback) as props.
 *   - Clicking a nav item calls `onSectionChange(sectionId)` which updates
 *     App.js state `activeSection`, causing the main content area to switch
 *     the rendered view component (e.g. ProjectDashboard, UnifiedCADView, etc.).
 *   - The active item is visually highlighted with a left accent bar and
 *     purple text/icon color (#667eea).
 */
import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
  Toolbar,
  Box,
  Divider,
  Chip,
  useTheme
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  FolderOpen,
  Assignment,
  Description,
  Workspaces,
  Route,
  VerifiedUser,
  Checklist,
  MenuBook,
  Architecture,
  Timeline,
  Insights,
} from '@mui/icons-material';

/** Fixed width of the sidebar drawer in pixels. */
const drawerWidth = 260;

/**
 * @param {string}   activeSection   - Current section ID from App.js state (e.g. 'projects', 'cad', 'parts').
 * @param {Function} onSectionChange - Callback invoked with a section ID string.
 *                                     In App.js this is `handleSectionChange` which calls
 *                                     `setActiveSection(section)` and persists to localStorage.
 */
const GlobalNavigation = ({
  activeSection,
  onSectionChange,
}) => {
  const theme = useTheme();

  /**
   * openSections -- controls which *category* groups are expanded/collapsed.
   * Keys match category IDs: 'workspace', 'lifecycle'.
   * All default to `true` (expanded) on mount.
   */
  const [openSections, setOpenSections] = useState({
    workspace: true,
    lifecycle: true,
  });

  /** Toggle a category group open/closed by its ID. */
  const handleToggle = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  /**
   * navItems -- defines all navigation items in the sidebar.
   *
   * Ordered by engineering lifecycle: define → design → validate → document.
   *
   * Each item maps to a section view rendered in App.js's main content area:
   *   'projects'     -> ProjectDashboard / ProjectView
   *   'requirements' -> RequirementsView      (source of truth)
   *   'cad'          -> UnifiedCADView         (design)
   *   'compliance'   -> ComplianceView         (validation)
   *   'documents'    -> DocumentsView          (deliverables)
   *   'orchestrator' -> OrchestratorView       (pipeline builder)
   *
   * The `category` field groups items under the matching category header.
   * The `badge` field renders a small gradient Chip next to the label.
   */
  const navItems = [
    { id: 'projects', label: 'Projects', icon: <FolderOpen />, category: 'workspace' },
    { id: 'analytics', label: 'Analytics', icon: <Insights />, standalone: true },
    { id: 'requirements', label: 'Requirements', icon: <Assignment />, category: 'lifecycle' },
    { id: 'cad', label: 'CAD', icon: <Architecture />, category: 'lifecycle' },
    { id: 'compliance', label: 'Compliance', icon: <VerifiedUser />, category: 'lifecycle' },
    { id: 'verification', label: 'Verification', icon: <Checklist />, category: 'lifecycle' },
    { id: 'documents', label: 'Documents', icon: <Description />, category: 'lifecycle' },
  ];

  /**
   * categories -- defines the collapsible group headers shown in the sidebar.
   * Each category groups a subset of navItems (matched by `navItem.category === category.id`).
   */
  const categories = [
    { id: 'workspace', label: 'Workspace', icon: <Workspaces sx={{ fontSize: 18 }} /> },
    { id: 'lifecycle', label: 'Engineering cycle', icon: <Timeline sx={{ fontSize: 18 }} /> },
  ];

  /**
   * renderNavItem -- renders a single nav item row.
   * Clicking calls onSectionChange(item.id) which sets App.js activeSection.
   */
  const renderNavItem = (item) => {
    const isActive = activeSection === item.id;
    return (
      <ListItemButton
        key={item.id}
        onClick={() => onSectionChange(item.id)}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          py: 1,
          pl: 2,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: isActive ? 'rgba(102, 126, 234, 0.12)' : 'transparent',
          /* Active indicator: thin gradient bar on left edge */
          '&::before': isActive ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 3,
            height: '60%',
            borderRadius: '0 4px 4px 0',
            background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
          } : {},
          '&:hover': {
            bgcolor: isActive
              ? 'rgba(102, 126, 234, 0.15)'
              : 'rgba(0, 0, 0, 0.04)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 36,
            color: isActive ? '#667eea' : 'text.secondary',
            transition: 'color 0.2s ease',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {item.label}
              {/*
               * Badge chip (e.g. "Preview" on Orchestrator).
               * Rendered only when item.badge is defined. Uses the app's
               * signature purple gradient. This visually signals that the
               * feature is in preview/beta state.
               */}
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    letterSpacing: '0.3px',
                  }}
                />
              )}
            </Box>
          }
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? '#667eea' : 'text.primary',
            component: 'div',
          }}
        />
      </ListItemButton>
    );
  };

  /**
   * drawerContent -- the full sidebar layout:
   *   <Toolbar />           spacer so content starts below the fixed AppBar
   *   <scrollable area>     iterates `categories`, each containing:
   *     - Collapsible category header (click to toggle)
   *     - Filtered navItems rendered via renderNavItem()
   *     - Divider between categories (except after the last one)
   *   <bottom spacing>
   */
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Empty Toolbar acts as a spacer to push content below the fixed AppBar */}
      <Toolbar />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 2 }}>
        {/* Render nav items in order — categories are collapsible groups, standalone items render directly */}
        {(() => {
          const elements = [];
          let lastWasCategory = false;

          // Build ordered render list: workspace category, then standalone items, then remaining categories
          const renderCategory = (category, isLast) => {
            elements.push(
              <Box key={category.id} sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleToggle(category.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    py: 1,
                    '&:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.08)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: '#667eea' }}>
                    {category.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={category.label}
                    primaryTypographyProps={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  />
                  {openSections[category.id] ? (
                    <ExpandLess sx={{ fontSize: 20, color: 'text.secondary' }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />
                  )}
                </ListItemButton>
                <Collapse in={openSections[category.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 1 }}>
                    {navItems.filter(i => i.category === category.id).map(item => renderNavItem(item))}
                  </List>
                </Collapse>
              </Box>
            );
          };

          // Workspace
          renderCategory(categories[0]);

          // Standalone items (analytics) — between workspace and lifecycle
          const standaloneItems = navItems.filter(i => i.standalone);
          if (standaloneItems.length > 0) {
            elements.push(
              <Divider key="div-standalone" sx={{ my: 1.5, mx: 1, borderColor: 'rgba(0, 0, 0, 0.06)' }} />
            );
            elements.push(
              <Box key="standalone" sx={{ px: 0.5, mb: 1 }}>
                {standaloneItems.map(item => renderNavItem(item))}
              </Box>
            );
          }

          // Remaining categories (lifecycle)
          elements.push(
            <Divider key="div-lifecycle" sx={{ my: 1.5, mx: 1, borderColor: 'rgba(0, 0, 0, 0.06)' }} />
          );
          for (let i = 1; i < categories.length; i++) {
            renderCategory(categories[i]);
          }

          return elements;
        })()}

        {/* Utility items — outside category groups */}
        <Divider sx={{ my: 1.5, mx: 1, borderColor: 'rgba(0, 0, 0, 0.06)' }} />
        <Box sx={{ px: 0.5 }}>
          {renderNavItem({ id: 'orchestrator', label: 'Automation', icon: <Route /> })}
          {renderNavItem({ id: 'knowledge-base', label: 'Knowledge Base', icon: <MenuBook /> })}
        </Box>
      </Box>

      {/* Bottom spacing */}
      <Box sx={{ pb: 4 }} />
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      data-tour="global-nav"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          /* Subtle vertical gradient background, adapts to dark/light mode */
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, #1e1e1e 0%, #121212 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default GlobalNavigation;
